(() => {
  if (customElements.get('kiwibit-listing-slider')) return;

  const SWIPE_DISTANCE = 50;
  const SWIPE_VELOCITY = 0.35;

  class KiwibitListingSlider extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.mode = this.dataset.mode || 'listing';
      this.slides = [...this.querySelectorAll('[data-role="slide"]')];
      this.tabs = [...this.querySelectorAll('[data-role="tab"]')];
      this.items = [...this.querySelectorAll('[data-role="item"]')];
      this.track = this.querySelector('[data-role="track"]');
      this.viewport = this.querySelector('[data-role="viewport"]');
      this.previousButton = this.querySelector('[data-role="previous"]');
      this.nextButton = this.querySelector('[data-role="next"]');
      this.status = this.querySelector('[data-role="status"]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = this.clampIndex(Number.parseInt(this.dataset.initialIndex || '0', 10));
      this.pointer = null;
      this.suppressNextClick = false;

      this.addEventListener('click', this.onClick, { signal: this.signal });
      this.addEventListener('keydown', this.onKeydown, { signal: this.signal });
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal: this.signal });
      document.addEventListener('shopify:section:select', this.onSectionSelect, { signal: this.signal });

      if (this.viewport && this.slideCount > 1 && this.mode !== 'layered') {
        this.viewport.addEventListener('pointerdown', this.onPointerDown, { signal: this.signal });
        this.viewport.addEventListener('pointermove', this.onPointerMove, { signal: this.signal });
        this.viewport.addEventListener('pointerup', this.onPointerEnd, { signal: this.signal });
        this.viewport.addEventListener('pointercancel', this.onPointerCancel, { signal: this.signal });
        this.viewport.addEventListener('click', this.onCapturedClick, { capture: true, signal: this.signal });
      }

      if (this.viewport && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateTrackPosition();
          if (this.mode === 'listing') this.updateViewportHeight();
        });
        this.resizeObserver.observe(this.viewport);
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      }

      this.classList.add('is-ready');
      this.goTo(this.activeIndex, 'initial', { animate: false, announce: false });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.heightFrame || 0);
      window.cancelAnimationFrame(this.trackFrame || 0);
      window.clearTimeout(this.suppressClickTimer);
      this.pointer = null;
    }

    get slideCount() {
      return this.slides.length || this.items.length || this.tabs.length;
    }

    clampIndex(index) {
      const parsed = Number.isFinite(index) ? index : 0;
      return Math.min(Math.max(parsed, 0), Math.max(this.slideCount - 1, 0));
    }

    goTo(index, source = 'api', options = {}) {
      if (this.slideCount === 0) return;

      const nextIndex = this.clampIndex(index);
      const shouldAnimate = options.animate !== false && !this.reduceMotion.matches && source !== 'theme-editor';
      this.activeIndex = nextIndex;
      this.dataset.activeIndex = String(nextIndex);
      this.dataset.motion = shouldAnimate ? 'animate' : 'immediate';
      this.style.setProperty('--kiwibit-listing-index', String(nextIndex));

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', !isActive);
        if (this.mode === 'layered') {
          slide.hidden = !isActive;
        }
      });

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === nextIndex;
        item.classList.toggle('is-active', isActive);
        item.dataset.active = String(isActive);
      });

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === nextIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      if (this.previousButton) this.previousButton.disabled = nextIndex === 0;
      if (this.nextButton) this.nextButton.disabled = nextIndex === this.slideCount - 1;

      if (this.mode !== 'layered') this.updateTrackPosition();
      if (this.mode === 'listing') this.updateViewportHeight();
      if (this.tabs[nextIndex]) this.revealTab(this.tabs[nextIndex], shouldAnimate && source !== 'initial');
      if (this.mode === 'layered' && source !== 'initial') this.revealLayeredItem(nextIndex, shouldAnimate);
      if (options.announce !== false) this.announce(nextIndex);

      this.dispatchEvent(new CustomEvent('kiwibit:slide-change', {
        bubbles: true,
        detail: { index: nextIndex, source },
      }));
    }

    onClick = (event) => {
      const previous = event.target.closest('[data-role="previous"]');
      const next = event.target.closest('[data-role="next"]');
      const tab = event.target.closest('[data-role="tab"]');

      if (previous && this.contains(previous)) this.goTo(this.activeIndex - 1, 'previous');
      if (next && this.contains(next)) this.goTo(this.activeIndex + 1, 'next');
      if (tab && this.contains(tab)) this.goTo(Number.parseInt(tab.dataset.index || '0', 10), 'tab');
    };

    onKeydown = (event) => {
      const tab = event.target.closest('[data-role="tab"]');
      const inViewport = this.viewport?.contains(event.target);
      if (!tab && !inViewport) return;

      const verticalTabs = this.mode === 'layered';
      const previousKey = verticalTabs ? 'ArrowUp' : 'ArrowLeft';
      const nextKey = verticalTabs ? 'ArrowDown' : 'ArrowRight';
      let nextIndex = null;

      if (event.key === previousKey) nextIndex = this.activeIndex - 1;
      if (event.key === nextKey) nextIndex = this.activeIndex + 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.slideCount - 1;
      if (tab && (event.key === 'Enter' || event.key === ' ')) nextIndex = Number.parseInt(tab.dataset.index || '0', 10);
      if (nextIndex === null) return;

      event.preventDefault();
      this.goTo(nextIndex, 'keyboard');
      this.tabs[this.activeIndex]?.focus({ preventScroll: true });
    };

    onPointerDown = (event) => {
      if (event.button !== 0 || !event.isPrimary) return;
      this.pointer = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: performance.now(),
        horizontal: false,
      };
    };

    onPointerMove = (event) => {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const deltaX = event.clientX - this.pointer.startX;
      const deltaY = event.clientY - this.pointer.startY;
      if (!this.pointer.horizontal && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.pointer.horizontal = true;
        this.viewport.setPointerCapture?.(event.pointerId);
      }
      if (this.pointer.horizontal) event.preventDefault();
    };

    onPointerEnd = (event) => {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const deltaX = event.clientX - this.pointer.startX;
      const elapsed = Math.max(performance.now() - this.pointer.startTime, 1);
      const velocity = Math.abs(deltaX) / elapsed;
      const isSwipe = this.pointer.horizontal && (Math.abs(deltaX) >= SWIPE_DISTANCE || velocity >= SWIPE_VELOCITY);

      if (isSwipe) {
        this.goTo(this.activeIndex + (deltaX < 0 ? 1 : -1), 'pointer');
        this.suppressNextClick = true;
        window.clearTimeout(this.suppressClickTimer);
        this.suppressClickTimer = window.setTimeout(() => {
          this.suppressNextClick = false;
        }, 400);
      }

      this.viewport.releasePointerCapture?.(event.pointerId);
      this.pointer = null;
    };

    onPointerCancel = (event) => {
      if (this.pointer?.id === event.pointerId) this.pointer = null;
    };

    onCapturedClick = (event) => {
      if (!this.suppressNextClick) return;
      if (event.target.closest('a')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      window.clearTimeout(this.suppressClickTimer);
      this.suppressNextClick = false;
    };

    onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const block = event.target.closest('[data-block-id]');
      if (!block) return;
      const index = [...this.querySelectorAll('[data-block-id]')]
        .find((candidate) => candidate.dataset.blockId === block.dataset.blockId)?.dataset.index;
      if (index !== undefined) this.goTo(Number.parseInt(index, 10), 'theme-editor', { animate: false });
    };

    onSectionSelect = (event) => {
      if (event.target === this || this.contains(event.target)) {
        this.goTo(this.activeIndex, 'theme-editor', { animate: false, announce: false });
      }
    };

    updateViewportHeight() {
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide || !this.viewport) return;
      window.cancelAnimationFrame(this.heightFrame || 0);
      this.heightFrame = window.requestAnimationFrame(() => {
        this.viewport.style.setProperty('--kiwibit-listing-active-height', `${activeSlide.offsetHeight}px`);
      });
    }

    updateTrackPosition() {
      if (!this.track || this.mode === 'layered') return;
      if (this.viewport) this.viewport.scrollLeft = 0;
      window.cancelAnimationFrame(this.trackFrame || 0);
      this.trackFrame = window.requestAnimationFrame(() => {
        const activeSlide = this.slides[this.activeIndex];
        if (!activeSlide) return;
        this.track.style.transform = `translate3d(${-activeSlide.offsetLeft}px, 0, 0)`;
      });
    }

    revealTab(tab, animate) {
      const tablist = tab.closest('[data-role="tablist"]');
      if (!tablist) return;
      const tabRect = tab.getBoundingClientRect();
      const tablistRect = tablist.getBoundingClientRect();
      const tabCenter = tabRect.left - tablistRect.left + tablist.scrollLeft + (tabRect.width / 2);
      const centeredLeft = tabCenter - (tablist.clientWidth / 2);
      const maxLeft = Math.max(tablist.scrollWidth - tablist.clientWidth, 0);
      const left = Math.min(Math.max(centeredLeft, 0), maxLeft);
      tablist.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
    }

    revealLayeredItem(index, animate) {
      if (!window.matchMedia('(max-width: 989.98px)').matches) return;
      const item = this.items[index];
      if (!item) return;
      const rect = item.getBoundingClientRect();
      const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!fullyVisible) item.scrollIntoView({ block: 'nearest', behavior: animate ? 'smooth' : 'auto' });
    }

    announce(index) {
      if (!this.status) return;
      this.status.textContent = `${this.status.dataset.slideLabel || 'Slide'} ${index + 1} ${this.status.dataset.ofLabel || 'of'} ${this.slideCount}`;
    }
  }

  customElements.define('kiwibit-listing-slider', KiwibitListingSlider);
})();
